import { useEffect, useState } from "react";
import styled from "styled-components";
import { FiDownload } from "react-icons/fi";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
} from "react-icons/ai";

import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import ViewContainer from "../../../Componentes/Utils/ViewContainer";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { formatarDataHora, formatResultadoTeste, formatTipoTeste } from "./utils";

const Titulo = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
`;

const ErrorMsg = styled.p`
    margin: 0;
    font-size: 13px;
    color: #b91c1c;
    text-align: center;
`;

const SecaoAnexos = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const AnexosLabel = styled.p`
    margin: 0 0 2px 2px;
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
`;

const SemAnexos = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;
`;

const CardAnexo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    min-width: 0;
`;

const AnexoIconWrap = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AnexoNome = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AnexoTamanho = styled.span`
    font-size: 11px;
    color: #64748b;
`;

const BotaoDownload = styled.a`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    color: #1d4ed8;
    background-color: rgba(29, 78, 216, 0.08);
    flex-shrink: 0;

    &:hover {
        background-color: rgba(29, 78, 216, 0.18);
    }
`;

function displayValue(value) {
    if (value == null || value === "") return "-";
    return String(value);
}

function formatarCpf(value) {
    if (!value) return "-";
    const d = String(value).replace(/\D/g, "");
    return d.length === 11 ? formatCPF(d) : value;
}

function formatarTamanho(bytes) {
    if (bytes == null || bytes === "" || Number.isNaN(Number(bytes))) return null;
    const b = Number(bytes);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function nomeArquivo(anexo) {
    if (anexo?.nome_original) return anexo.nome_original;
    const caminho = anexo?.caminho ?? "";
    const parts = String(caminho).split("/");
    return parts[parts.length - 1] || "Arquivo";
}

function getFileIcon(filename) {
    const name = String(filename ?? "").toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name))
        return <AiOutlineFileImage size={20} color="#4a90e2" />;
    if (name.endsWith(".pdf"))
        return <AiOutlineFilePdf size={20} color="#e63946" />;
    if (/\.(doc|docx)$/i.test(name))
        return <AiOutlineFileWord size={20} color="#2a6db0" />;
    if (/\.(xls|xlsx)$/i.test(name))
        return <AiOutlineFileExcel size={20} color="#207245" />;
    return <AiOutlineFileUnknown size={20} color="#555" />;
}

const ModalViewTeste = ({ testeId, fechar }) => {
    const api = useApi();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [teste, setTeste] = useState(null);

    useEffect(() => {
        if (!testeId) return;

        const carregar = async () => {
            setLoading(true);
            setErro(null);
            setTeste(null);

            try {
                const { data } = await api.get("/Painel/API/AtendimentoAmb/TestesAlcoolDrogas/Get/index.php", {
                    params: { id: testeId },
                });

                if (!data?.success || !data?.data) {
                    const msg = data?.message || "Teste não encontrado.";
                    setErro(msg);
                    notifyError(msg);
                    return;
                }

                setTeste(data.data);
            } catch (err) {
                console.error(err);
                const msg = err?.response?.data?.message || "Erro ao carregar teste.";
                setErro(msg);
                notifyError(msg);
            } finally {
                setLoading(false);
            }
        };

        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testeId]);

    const anexos = Array.isArray(teste?.anexos) ? teste.anexos : [];

    return (
        <Overlay state>
            <Modal header close={fechar} size="md">
                <Titulo>VISUALIZAR TESTE</Titulo>

                {loading && <SmallLoading text="Carregando teste…" />}

                {!loading && erro && <ErrorMsg>{erro}</ErrorMsg>}

                {!loading && !erro && teste && (
                    <Container>
                        <ViewContainer label="ID" valor={teste.id} />
                        <ViewContainer label="CPF" valor={formatarCpf(teste.cpf)} />
                        <ViewContainer label="NOME" valor={displayValue(teste.nome)} />
                        <ViewContainer label="FUNÇÃO" valor={displayValue(teste.funcao)} />
                        <ViewContainer label="CENTRO DE CUSTO" valor={displayValue(teste.centro_custo)} />
                        <ViewContainer label="CONTRATO" valor={displayValue(teste.contrato)} />
                        <ViewContainer label="LÍDER" valor={displayValue(teste.lider)} />
                        <ViewContainer label="TIPO DE TESTE" valor={formatTipoTeste(teste.tipo_teste)} />
                        <ViewContainer label="RESULTADO" valor={formatResultadoTeste(teste.resultado)} />
                        <ViewContainer label="OBSERVAÇÕES" valor={displayValue(teste.observacoes)} />
                        <ViewContainer label="DATA / HORA" valor={formatarDataHora(teste.data_hora)} />

                        <SecaoAnexos>
                            <AnexosLabel>ANEXOS</AnexosLabel>

                            {anexos.length === 0 ? (
                                <SemAnexos>Nenhum anexo.</SemAnexos>
                            ) : (
                                anexos.map((anexo) => {
                                    const nome = nomeArquivo(anexo);
                                    const tamanho = formatarTamanho(anexo.tamanho);
                                    return (
                                        <CardAnexo key={anexo.id ?? anexo.caminho}>
                                            <AnexoIconWrap>{getFileIcon(nome)}</AnexoIconWrap>
                                            <AnexoInfo>
                                                <AnexoNome title={nome}>{nome}</AnexoNome>
                                                {tamanho && <AnexoTamanho>{tamanho}</AnexoTamanho>}
                                            </AnexoInfo>
                                            <BotaoDownload
                                                href={anexo.caminho}
                                                download
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Baixar anexo"
                                            >
                                                <FiDownload size={14} />
                                            </BotaoDownload>
                                        </CardAnexo>
                                    );
                                })
                            )}
                        </SecaoAnexos>
                    </Container>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalViewTeste;
